import { Injectable, inject } from '@angular/core';
import { HttpClient, HttpParams } from '@angular/common/http';
import { Observable, map } from 'rxjs';
import { Branch } from '@shared-models';
import { API_CONFIG, ApiConfig } from '../api-config';
import { ApiResponse } from '@shared-models';

interface PageableData {
  content: any[];
  page: number;
  size: number;
  totalElements: number;
  totalPages: number;
  first: boolean;
  last: boolean;
  empty: boolean;
}

/**
 * Tipo de retorno para paginación server-side
 */
export interface BranchPageResponse {
  items: Branch[];
  page: number;
  size: number;
  totalElements: number;
  totalPages: number;
}

@Injectable({ providedIn: 'root' })
export class BranchService {
  private readonly httpClient = inject(HttpClient);
  private readonly apiConfig = inject<ApiConfig>(API_CONFIG);

  private get apiUrl(): string {
    return `${this.apiConfig.apiUrl}/structure/branches`;
  }

  /**
   * Normaliza el status del API al modelo del frontend
   * API returns: "ACTIVE" | "INACTIVE"
   * Frontend: 'active' | 'inactive'
   */
  private normalizeStatus(status: string | undefined): 'active' | 'inactive' {
    if (!status) return 'active';
    return status.toUpperCase() === 'INACTIVE' ? 'inactive' : 'active';
  }

  /**
   * Transforma la respuesta del backend al modelo del frontend
   */
  private mapToBranch(item: any): Branch {
    return {
      id: item.id,
      code: item.code || '',
      name: item.tradeName || item.name || '',
      tradeName: item.tradeName || item.name || '',
      companyName: item.companyName || '',
      groupId: item.groupId,
      groupName: item.groupName || '',
      country: item.countryName || item.countryLargeIso || item.country || '',
      countryId: item.countryId,
      province: item.provinceName || item.province || '',
      provinceId: item.provinceId,
      locality: item.localityName || item.locality || '',
      localityId: item.localityId,
      address: item.address,
      positionsCount: item.positions ?? item.positionsCount ?? 0,
      cashiersCount: item.boxes ?? item.cashiers ?? item.cashiersCount ?? 0,
      atmCount: item.atms ?? item.atm ?? item.atmCount ?? 0,
      status: this.normalizeStatus(item.status),
      companyId: item.companyId,
      contacts: Array.isArray(item.contacts)
        ? item.contacts.map((c: any) => ({
            id: c.id,
            name: c.name || '',
            email: c.email || '',
            lada: c.lada || '+54',
            phoneNumber: c.phoneNumber || '',
            position: c.position || '',
            primary: c.primary ?? false,
          }))
        : undefined,
    };
  }

  /**
   * Obtiene sucursales con paginación server-side
   */
  getAll(
    page = 0,
    size = 20,
    sort = 'createdAt',
    direction: 'asc' | 'desc' = 'desc',
    search?: string,
    status?: string,
    type?: string,
  ): Observable<BranchPageResponse> {
    let params = new HttpParams()
      .set('page', page.toString())
      .set('size', size.toString())
      .set('sort', sort)
      .set('direction', direction);

    if (search && search.trim()) {
      params = params.set('search', search.trim());
    }
    if (status && status.trim()) {
      params = params.set('status', status.trim());
    }
    if (type && type.trim()) {
      params = params.set('type', type.trim());
    }

    return this.httpClient
      .get<ApiResponse<PageableData>>(this.apiUrl, { params })
      .pipe(
        map((response) => {
          const pageableData = response.data;
          const items = pageableData?.content || [];
          return {
            items: items.map((item: any) => this.mapToBranch(item)),
            page: pageableData?.page ?? 0,
            size: pageableData?.size ?? size,
            totalElements: pageableData?.totalElements ?? 0,
            totalPages: pageableData?.totalPages ?? 0,
          };
        }),
      );
  }

  /**
   * Obtiene una sucursal por ID
   */
  getById(id: string): Observable<Branch> {
    return this.httpClient
      .get<ApiResponse<any>>(`${this.apiUrl}/${id}`)
      .pipe(map((response) => this.mapToBranch(response.data)));
  }

  /**
   * Serializa el payload como FormData (multipart/form-data).
   * Mismo patrón que CompanyService.toFormData().
   */
  private toFormData(data: Record<string, any>): FormData {
    const formData = new FormData();
    for (const [key, value] of Object.entries(data)) {
      this.appendToFormData(formData, key, value);
    }
    return formData;
  }

  private appendToFormData(
    formData: FormData,
    key: string,
    value: unknown,
  ): void {
    if (value === null || value === undefined) return;

    if (value instanceof File || value instanceof Blob) {
      formData.append(key, value);
      return;
    }

    if (Array.isArray(value)) {
      value.forEach((item, index) => {
        if (item === null || item === undefined) return;
        if (
          typeof item === 'object' &&
          !(item instanceof File) &&
          !(item instanceof Blob)
        ) {
          for (const [subKey, subValue] of Object.entries(
            item as Record<string, unknown>,
          )) {
            this.appendToFormData(
              formData,
              `${key}[${index}].${subKey}`,
              subValue,
            );
          }
        } else {
          this.appendToFormData(formData, `${key}[${index}]`, item);
        }
      });
      return;
    }

    if (typeof value === 'object') {
      for (const [subKey, subValue] of Object.entries(
        value as Record<string, unknown>,
      )) {
        this.appendToFormData(formData, `${key}.${subKey}`, subValue);
      }
      return;
    }

    formData.append(key, String(value));
  }

  /**
   * Mapea los campos del frontend al formato que espera el backend.
   *
   * Frontend (BranchFormValue):           Backend (API):
   *   group        → groupId
   *   company      → companyId
   *   country      → countryId
   *   province     → provinceId
   *   locality     → localityId
   *   contacts[i].phonePrefix → contacts[i].lada
   *   contacts[i].isPrimary   → contacts[i].primary
   */
  private mapToApiPayload(data: Record<string, any>): Record<string, any> {
    const mapped: Record<string, any> = { ...data };

    // Mapear IDs de selects
    if (mapped['group']) mapped['groupId'] = mapped['group'];
    if (mapped['company']) mapped['companyId'] = mapped['company'];
    if (mapped['country']) mapped['countryId'] = mapped['country'];
    if (mapped['province']) mapped['provinceId'] = mapped['province'];
    if (mapped['locality']) mapped['localityId'] = mapped['locality'];

    // Eliminar campos UI-only / sub-recursos que no debe recibir el PUT general
    delete mapped['group'];
    delete mapped['company'];
    delete mapped['country'];
    delete mapped['province'];
    delete mapped['locality'];
    delete mapped['lat'];
    delete mapped['lng'];
    delete mapped['scheduleAtencion'];
    delete mapped['schedulePagos'];
    delete mapped['holidays'];
    delete mapped['specialAtencion'];
    delete mapped['specialPagos'];

    // Mapear contactos
    if (Array.isArray(mapped['contacts'])) {
      mapped['contacts'] = mapped['contacts'].map((c: any) => {
        const contact: Record<string, any> = { ...c };
        if (contact['phonePrefix'] !== undefined) {
          contact['lada'] = contact['phonePrefix'];
          delete contact['phonePrefix'];
        }
        if (contact['isPrimary'] !== undefined) {
          contact['primary'] = contact['isPrimary'];
          delete contact['isPrimary'];
        }
        return contact;
      });
    }

    return mapped;
  }

  /**
   * Crea una nueva sucursal
   */
  create(data: Partial<Branch>): Observable<{ message: string }> {
    const payload = this.mapToApiPayload(data as Record<string, any>);
    const formData = this.toFormData(payload);
    return this.httpClient.post<ApiResponse<any>>(this.apiUrl, formData).pipe(
      map((response) => ({
        message: response.message || 'Sucursal creada exitosamente',
      })),
    );
  }

  /**
   * Actualiza una sucursal existente
   */
  update(id: string, data: Partial<Branch>): Observable<{ message: string }> {
    const payload = this.mapToApiPayload(data as Record<string, any>);
    const formData = this.toFormData(payload);
    return this.httpClient
      .put<ApiResponse<any>>(`${this.apiUrl}/${id}`, formData)
      .pipe(
        map((response) => ({
          message: response.message || 'Sucursal actualizada exitosamente',
        })),
      );
  }

  /**
   * Elimina una sucursal
   */
  delete(id: string): Observable<{ message: string }> {
    return this.httpClient
      .delete<{ success: boolean; message: string }>(`${this.apiUrl}/${id}`)
      .pipe(
        map((response) => ({
          message: response.message || 'Sucursal eliminada exitosamente',
        })),
      );
  }

  /**
   * Actualiza el status de una sucursal
   */
  updateStatus(
    id: string,
    status: 'active' | 'inactive',
  ): Observable<{ message: string }> {
    const apiStatus = status === 'inactive' ? 'INACTIVE' : 'ACTIVE';
    return this.httpClient
      .patch<{
        success: boolean;
        message: string;
      }>(`${this.apiUrl}/${id}/status`, { status: apiStatus })
      .pipe(
        map((response) => ({
          message: response.message || 'Status actualizado exitosamente',
        })),
      );
  }

  /**
   * Exporta sucursales a PDF
   */
  exportPdf(options?: {
    title?: string;
    status?: string;
    search?: string;
    type?: string;
  }): Observable<Blob> {
    let params = new HttpParams()
      .set('title', options?.title || 'Reporte de sucursales')
      .set('status', options?.status || '')
      .set('search', options?.search || '');
    if (options?.type?.trim()) {
      params = params.set('type', options.type.trim());
    }

    return this.httpClient.get(`${this.apiConfig.apiUrl}/export/pdf/branch`, {
      params,
      responseType: 'blob',
    });
  }

  /**
   * Exporta sucursales a Excel
   */
  exportExcel(options?: {
    title?: string;
    status?: string;
    search?: string;
    type?: string;
  }): Observable<Blob> {
    let params = new HttpParams()
      .set('title', options?.title || 'Reporte de sucursales')
      .set('status', options?.status || '')
      .set('search', options?.search || '');
    if (options?.type?.trim()) {
      params = params.set('type', options.type.trim());
    }

    return this.httpClient.get(`${this.apiConfig.apiUrl}/export/excel/branch`, {
      params,
      responseType: 'blob',
    });
  }
}
